import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import classnames from "classnames/bind";

import OuterClickListener from "@components/outer-click-listener";
import Icon from "@components/icon";
import Counter from "@components/counter";

import { locale } from "../../../../locales/strings";

import { useStore } from "../../../../connect";

import styles from "./MainMenuButton.scss";

const cx = classnames.bind(styles);

const MainMenuButton = forwardRef(
  (
    {
      dropdownId,
      dropdownComponent,
      dropdownTriggerOnClick,
      label,
      smallLabel,
      href,
      isFullWidth,
      className,
      counter,
      isFullOnDesktop,
      onClick,
    },
    ref
  ) => {
    const { header } = useStore();

    const isForeignLocale = locale !== "ru";

    const handleDropdownTriggerClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      dropdownTriggerOnClick?.();
      header.toggleDropdown(dropdownId);
    };

    const labelElement = smallLabel ? (
      <>
        <span className={cx("mobile-button-text")}>{smallLabel}</span>
        <span className={cx("desktop-button-text")}>{label}</span>
      </>
    ) : (
      label
    );

    if (dropdownComponent) {
      const isDropdownOpen = header.openedDropdown === dropdownId;

      return (
        <div
          ref={ref}
          className={cx(
            "main-menu-button-container",
            {
              "dropdown-active": isDropdownOpen,
              "is-full-on-desktop": isFullOnDesktop,
            },
            className
          )}
        >
          <button
            onClick={handleDropdownTriggerClick}
            className={cx("main-menu-button", {
              active: isDropdownOpen,
              "foreign-locale": isForeignLocale,
            })}
          >
            <span className={cx("label-text")}>{labelElement}</span>
            {counter > 0 && (
              <Counter maxValue={99} className={cx("counter")} active>
                {counter}
              </Counter>
            )}
            <Icon
              name={isDropdownOpen ? "ic_arrow-up-out" : "ic_arrow-down-out"}
              size="s"
              color="var(--glz-color-neutral-tone-5)"
            />
          </button>
          {isDropdownOpen && (
            <OuterClickListener
              onClickOutside={header.closeDropdown}
              className={cx("main-menu-dropdown")}
            >
              {dropdownComponent}
            </OuterClickListener>
          )}
        </div>
      );
    }

    if (onClick && !href) {
      return (
        <div
          ref={ref}
          className={cx(
            "main-menu-button-container",
            {
              "is-full-on-desktop": isFullOnDesktop,
            },
            className
          )}
        >
          <button
            onClick={onClick}
            className={cx("main-menu-button", {
              "foreign-locale": isForeignLocale,
            })}
          >
            <span className={cx("label-text")}>{labelElement}</span>
          </button>
        </div>
      );
    }

    return (
      <a
        className={cx(
          "main-menu-link",
          {
            "is-full-width": isFullWidth,
            "is-full-on-desktop": isFullOnDesktop,
            "foreign-locale": isForeignLocale,
          },
          className
        )}
        href={href}
        onClick={onClick}
      >
        {labelElement}
      </a>
    );
  }
);

MainMenuButton.propTypes = {
  dropdownComponent: PropTypes.node,
  label: PropTypes.string,
  href: PropTypes.string,
  isFullWidth: PropTypes.bool,
  className: PropTypes.string,
  smallLabel: PropTypes.string,
  dropdownId: PropTypes.string,
  dropdownTriggerOnClick: PropTypes.func,
  counter: PropTypes.number,
  isFullOnDesktop: PropTypes.bool,
  onClick: PropTypes.func,
};

export default observer(MainMenuButton);
